<script setup lang="ts">
import AiAdviserChat from '@/domains/student-app/components/adviser/AiAdviserChat.vue';
import AiAdviserChatComposeInput from '@/domains/student-app/components/adviser/AiAdviserChatComposeInput.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import Loading from '@/components/loading/Loading.vue';

import AdviserIcon from '@/icons/solid/adviser.svg';
import Badge from '@/components/badge/Badge.vue';
import { BadgeColor, BadgeSize, BadgeVariant } from '@/hooks/useBadgeClasses';

import { useAiChat } from '@/domains/student-app/composables/useAiChat';
import { useChatBot } from '@/domains/chat/composables/useChatBot';

const {
  isOpen,
  chat,
  messages,
  userInput,
  messagesEnd,
  messagesContainer,
  composeInput,
  messageLoading,
  chatLoading,
  hideChat,
  send,
  rateMessage
} = useAiChat();

defineProps<{
  isStacked?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { chatBotName } = useChatBot();

onMounted(() => {
  if (isOpen.value) {
    nextTick(() => {
      messagesEnd.value?.scrollIntoView({ behavior: 'instant' });
    });
  }
});

function handleClose() {
  hideChat();
  emit('close');
}
</script>

<template>
  <teleport to="body">
    <Dialog
      :title="chat?.title ?? 'Chat with Adviser'"
      :isOpen="isOpen"
      :headerIcon="AdviserIcon"
      :hideFooter="true"
      :isStacked="isStacked"
      fit="fill"
      @onClose="handleClose"
      @onCancel="handleClose"
    >
      <template #chat>
        <div v-if="chatLoading" class="flex h-full flex-col justify-center">
          <Loading title="Loading chat..." />
        </div>
        <div v-else ref="messagesContainer" class="h-full divide-y divide-slate-200 bg-slate-50">
          <div class="flex flex-col gap-2 px-5 py-7">
            <div class="flex items-center gap-2">
              <h2 class="text-2xl font-semibold leading-7 text-slate-900">{{ chatBotName }}</h2>
              <Badge :color="BadgeColor.slate" :size="BadgeSize.md" :variant="BadgeVariant.soft"
                >AI adviser</Badge
              >
            </div>
            <p class="text-md leading-6 text-slate-700">
              This is your conversation with
              <span class="font-bold text-slate-900">{{ chatBotName }}</span
              >. Ask a question or write your first message.
            </p>
          </div>
          <div class="pt-5">
            <AiAdviserChat :messages="messages" :isLoading="messageLoading" @rate="rateMessage" />
            <div ref="messagesEnd"></div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="border-t border-slate-200 p-4">
          <AiAdviserChatComposeInput
            ref="composeInput"
            v-model="userInput"
            :isLoading="messageLoading"
            @send="send"
          />
        </div>
      </template>
    </Dialog>
  </teleport>
</template>
